/**
 * USERS CONTEXT
 * * Scopo del file:
 * Questo contesto funge da gestore centrale per i dati relativi allo staff (Hairdressers) e agli inviti.
 * * Funzionalità principali:
 * 1. Gestione Staff: Caricamento, creazione, aggiornamento e disattivazione degli utenti dalla tabella 'Hairdressers'.
 * 2. Sincronizzazione Auth: Quando un utente aggiorna il proprio profilo (es. avatar), questo context si assicura
 * di aggiornare anche l'utente di sessione (AuthContext) per riflettere le modifiche nella UI (TopBar).
 * 3. Gestione Inviti: Invio e revoca degli inviti via email per nuovi membri dello staff.
 * 4. Gestione Banner: Upload e cancellazione del banner del negozio nello Storage.
 */

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { useBilling } from './BillingContext';
import {
    validateUserInviteLimit,
    validateFileSize,
    getShopBannerPath,
    getShopLogoPath,
    getShopGalleryImagePath,
    deleteUserService
} from '@/services/users/userService';
import { optimizeImage } from '@/lib/imageOptimizer';

const UsersContext = createContext<any | null>(null);

export const useUsers = () => {
    const context = useContext(UsersContext);
    if (!context) {
        throw new Error('useUsers must be used within a UsersProvider');
    }
    return context;
};

export const UsersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        organization,
        isAuthenticated,
        user: currentUser,
        updateCurrentUser, // Fondamentale per aggiornare l'avatar nella TopBar
        updateOrganization,
    } = useAuth();
    const { subscription } = useBilling();
    const [users, setUsers] = useState<any[]>([]);
    const [invites, setInvites] = useState<any[]>([]);
    const [gallery, setGallery] = useState<{ id: number; image_url: string }[]>([]);

    // Caricamento dati iniziale
    useEffect(() => {
        if (isAuthenticated && organization?.id) {
            loadData();
        }
    }, [isAuthenticated, organization?.id]);

    const loadData = async () => {
        try {
            // Carica utenti (Hairdressers)
            // sort_order = ordine manuale deciso dall'admin in agenda; chi non ce l'ha
            // finisce in coda e viene poi ordinato per nome dai consumatori.
            const { data: userData, error: userError } = await supabase
                .from('Hairdressers')
                .select('*')
                .eq('shop_id', organization!.id)
                .order('sort_order', { ascending: true, nullsFirst: false });

            if (userError) throw userError;
            setUsers(userData || []);

            // Carica inviti
            const { data: invitesData, error: invitesError } = await supabase
                .from('Invites')
                .select('*')
                .eq('shop_id', organization!.id);

            if (invitesError) throw invitesError;
            setInvites(invitesData || []);

            // Carica Gallery
            const { data: galleryData, error: galleryError } = await supabase
                .from('ShopGallery')
                .select('*')
                .eq('shop_id', organization!.id)
                .order('id', { ascending: true });

            if (galleryError) throw galleryError;
            setGallery(galleryData || []);
        } catch (error) {
            console.error('Errore nel caricamento degli utenti:', error);
        }
    };

    // --- AGGIORNAMENTO UTENTE (Avatar, Nome, Ecc.) ---
    const updateUser = async (id: string, formData: any) => {
        try {

            // 1. Aggiorna su Supabase (Tabella Hairdressers)
            const { data, error } = await supabase
                .from('Hairdressers')
                .update(formData)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error("[UsersContext] Errore update Supabase:", error);
                throw new Error(error.message);
            }

            // 2. Aggiorna lo stato locale della lista utenti
            setUsers((prevUsers) =>
                prevUsers.map((user) => (user.id === id ? data : user)),
            );

            // 3. CRUCIALE: Se l'utente modificato è quello loggato, aggiorna AuthContext
            // Questo fa sì che TopBar e Profile ricevano subito il nuovo avatar/nome
            if (currentUser && (currentUser as any).id === id) {
                updateCurrentUser(data);
            }

            return { success: true, error: '' };
        } catch (error: any) {
            console.error("Errore nell'aggiornamento dell'utente:", error);
            return { success: false, error: error?.message || "Errore sconosciuto" };
        }
    };

    const inviteUser = async (email: string, role: 'admin' | 'user' = 'user') => {
        try {
            // Gli inviti pendenti (non usati, non scaduti) occupano un posto del limite
            const now = new Date();
            const pendingInvites = invites.filter((inv: any) => !inv.used && now < new Date(inv.expires_at)).length;
            const validation = validateUserInviteLimit(users, subscription?.plan_id, pendingInvites);
            if (!validation.isValid) {
                throw new Error(validation.error || 'Errore di validazione limite utenti');
            }

            const { data, error } = await supabase.functions.invoke('send-invite', {
                body: {
                    email,
                    shop_id: organization!.id,
                    shop_name: organization!.name,
                    origin: window.location.origin,
                    role,
                },
            });

            if (error) throw new Error(error?.message || "Errore durante l'invio dell'invito");
            if (!data?.success) throw new Error(data?.error || "Errore durante l'invio dell'invito");

            if (data.data?.invite) {
                setInvites((prevInvites) => [...prevInvites, data.data.invite]);
            }

            return { success: true, error: '' };
        } catch (error: any) {
            return { success: false, error: error?.message };
        }
    };

    const deleteInvite = async (token: string) => {
        try {
            const { error } = await supabase
                .from('Invites')
                .delete()
                .eq('token', token);

            if (error) throw new Error();

            setInvites((prev) => prev.filter((invite) => invite.token !== token));
            return { success: true, error: '' };
        } catch (error: any) {
            console.error("Errore nell'eliminazione dell'invito:", error);
            return { success: false, error: error?.message };
        }
    };

    const deleteUser = async (id: string) => {
        try {
            // Controlla se esistono prenotazioni per questo utente
            const { count: bookingsCount, error: countError } = await supabase
                .from('Bookings')
                .select('*', { count: 'exact', head: true })
                .eq('hairdresser_id', id);

            if (countError) throw countError;

            const result = await deleteUserService(id, bookingsCount);

            if (!result.success) throw new Error(result.error);

            setUsers((prev) => prev.filter((user) => user.id !== id));
            return { success: true, error: '' };
        } catch (error: any) {
            console.error("Errore nell'eliminazione dell'utente:", error);
            return { success: false, error: error?.message };
        }
    };

    const getUserById = (id: string) =>
        users.find((user) => user.id == id);

    // --- GESTIONE BANNER ---
    const uploadShopBanner = async (file: File) => {
        try {
            // Controlla dimensione file
            const validation = validateFileSize(file, 10);
            if (!validation.isValid) throw new Error(validation.error || 'File non valido');

            // Comprimi l'immagine prima di inviarla (se necessario)
            const optimizedFile = await optimizeImage(file);

            const { dir, namePrefix, fileName } = getShopBannerPath(organization!.id, optimizedFile.type);

            // Pulisci vecchi file
            const { data: existingFiles, error: listError } = await supabase.storage
                .from('shops_banners')
                .list(dir, { limit: 100 });

            if (!listError && Array.isArray(existingFiles)) {
                const toRemove = existingFiles
                    .filter((obj) => obj.name?.startsWith(namePrefix))
                    .map((obj) => `${dir}/${obj.name}`);
                if (toRemove.length) {
                    await supabase.storage.from('shops_banners').remove(toRemove);
                }
            }

            // Upload nuovo file
            const { error: uploadError } = await supabase.storage
                .from('shops_banners')
                .upload(fileName, optimizedFile, {
                    upsert: true,
                    contentType: optimizedFile.type
                });

            if (uploadError) throw uploadError;

            // Ottieni URL pubblico
            const { data: publicData } = supabase.storage
                .from('shops_banners')
                .getPublicUrl(fileName);

            const publicUrl = publicData?.publicUrl;
            if (!publicUrl) throw new Error("Impossibile ottenere l'URL pubblico dell'immagine");

            // Aggiorna organizzazione
            const timestampUrl = `${publicUrl}?t=${new Date().getTime()}`;
            const result = await updateOrganization({ banner_url: timestampUrl });
            if (!result.success) throw new Error('Errore nell\'aggiornamento del banner');

            return { success: true, url: timestampUrl };
        } catch (error: any) {
            console.error('Errore upload banner shop:', error);
            return { success: false, error: error?.message };
        }
    };

    const deleteShopBanner = async () => {
        try {
            // Ricava l'estensione dall'URL salvato, come in deleteGalleryImage
            const ext = organization?.banner_url?.split('.').pop()?.split('?')[0];
            const fileType = ext ? `image/${ext}` : 'image/webp';
            const { fileName } = getShopBannerPath(organization!.id, fileType);

            await supabase.storage.from('shops_banners').remove([fileName]);

            const result = await updateOrganization({ banner_url: null });
            if (!result.success) throw new Error('Errore nell\'aggiornamento del banner');
            return { success: true };
        } catch (error: any) {
            console.error('Errore delete banner shop:', error);
            return { success: false, error: error?.message };
        }
    };

    // --- GESTIONE LOGO ---
    const uploadShopLogo = async (file: File) => {
        try {
            // Controlla dimensione file
            const validation = validateFileSize(file, 10);
            if (!validation.isValid) throw new Error(validation.error || 'File non valido');

            // Comprimi l'immagine prima di inviarla (se necessario)
            const optimizedFile = await optimizeImage(file);

            const { dir, namePrefix, fileName } = getShopLogoPath(organization!.id, optimizedFile.type);

            // Pulisci vecchi file
            const { data: existingFiles, error: listError } = await supabase.storage
                .from('shop-logos')
                .list(dir, { limit: 100 });

            if (!listError && Array.isArray(existingFiles)) {
                const toRemove = existingFiles
                    .filter((obj) => obj.name?.startsWith(namePrefix))
                    .map((obj) => `${dir}/${obj.name}`);
                if (toRemove.length) {
                    await supabase.storage.from('shop-logos').remove(toRemove);
                }
            }

            // Upload nuovo file
            const { error: uploadError } = await supabase.storage
                .from('shop-logos')
                .upload(fileName, optimizedFile, {
                    upsert: true,
                    contentType: optimizedFile.type
                });

            if (uploadError) throw uploadError;

            // Ottieni URL pubblico
            const { data: publicData } = supabase.storage
                .from('shop-logos')
                .getPublicUrl(fileName);

            const publicUrl = publicData?.publicUrl;
            if (!publicUrl) throw new Error("Impossibile ottenere l'URL pubblico del logo");

            // Aggiorna organizzazione
            const timestampUrl = `${publicUrl}?t=${new Date().getTime()}`;
            const result = await updateOrganization({ logo_url: timestampUrl });
            if (!result.success) throw new Error('Errore nell\'aggiornamento del logo');

            return { success: true, url: timestampUrl };
        } catch (error: any) {
            console.error('Errore upload logo shop:', error);
            return { success: false, error: error?.message };
        }
    };

    const deleteShopLogo = async () => {
        try {
            // Ricava l'estensione dall'URL salvato, come in deleteGalleryImage
            const ext = organization?.logo_url?.split('.').pop()?.split('?')[0];
            const fileType = ext ? `image/${ext}` : 'image/webp';
            const { fileName } = getShopLogoPath(organization!.id, fileType);

            await supabase.storage.from('shop-logos').remove([fileName]);

            const result = await updateOrganization({ logo_url: null });
            if (!result.success) throw new Error('Errore nell\'aggiornamento del logo');
            return { success: true };
        } catch (error: any) {
            console.error('Errore delete logo shop:', error);
            return { success: false, error: error?.message };
        }
    };

    // --- GESTIONE GALLERY ---
    const uploadGalleryImage = async (file: File) => {
        try {
            // Controlla dimensione file
            const validation = validateFileSize(file, 10);
            if (!validation.isValid) throw new Error(validation.error || 'File non valido');

            if (gallery.length >= 4) throw new Error("Hai già raggiunto il limite di 4 immagini");

            // 1. Inserisci record temporaneo per ottenere l'ID
            const { data: insertData, error: insertError } = await supabase
                .from('ShopGallery')
                .insert({
                    shop_id: organization!.id,
                    image_url: 'pending'
                })
                .select()
                .single();

            if (insertError) throw insertError;
            const item = insertData;

            // Comprimi immagine prima di caricarla
            const optimizedFile = await optimizeImage(file);

            // 2. Costruisci il path coerente con il fileType dell'immagine compressa
            const fileName = getShopGalleryImagePath(organization!.id, item.id, optimizedFile.type);

            // 3. Upload su Storage
            const { error: uploadError } = await supabase.storage
                .from('shop-gallery')
                .upload(fileName, optimizedFile, {
                    upsert: true,
                    contentType: optimizedFile.type,
                });

            if (uploadError) {
                // Rollback db
                await supabase.from('ShopGallery').delete().eq('id', item.id);
                throw uploadError;
            }

            // 4. Ottieni URL pubblico
            const { data: publicData } = supabase.storage
                .from('shop-gallery')
                .getPublicUrl(fileName);

            const publicUrl = publicData?.publicUrl;

            // 5. Aggiorna record con URL corretto
            const { data: finalData, error: updateError } = await supabase
                .from('ShopGallery')
                .update({ image_url: publicUrl })
                .eq('id', item.id)
                .select()
                .single();

            if (updateError) throw updateError;

            setGallery(prev => [...prev, finalData]);
            return { success: true };

        } catch (error: any) {
            console.error('Errore update gallery:', error);
            return { success: false, error: error?.message || "Errore sconosciuto" };
        }
    };

    const deleteGalleryImage = async (id: number) => {
        try {
            // 1. Ricava il fileType dall'image_url salvato nello stato per ricostruire
            //    il path con la stessa estensione usata all'upload.
            const galleryItem = gallery.find((img) => img.id === id);
            const ext = galleryItem?.image_url?.split('.').pop()?.split('?')[0];
            const fileType = ext ? `image/${ext}` : 'image/webp';
            const fileName = getShopGalleryImagePath(organization!.id, id, fileType);

            // 2. Rimuovi da Storage
            const { error: storageError } = await supabase.storage
                .from('shop-gallery')
                .remove([fileName]);

            if (storageError) console.error("Errore rimozione file storage (proseguo comunque col db):", storageError);

            // 3. Rimuovi da DB
            const { error: dbError } = await supabase
                .from('ShopGallery')
                .delete()
                .eq('id', id);

            if (dbError) throw dbError;

            setGallery(prev => prev.filter(img => img.id !== id));
            return { success: true };

        } catch (error: any) {
            console.error('Errore delete gallery image:', error);
            return { success: false, error: error?.message };
        }
    };

    // --- ORDINE MANUALE DELLO STAFF IN AGENDA ---
    // Riceve gli id nell'ordine voluto e scrive sort_order su ciascuno.
    // Lo stato locale si aggiorna subito (ottimistico): l'agenda si riordina
    // mentre la scrittura viaggia, e in caso di errore si ricarica dal DB.
    const updateStaffOrder = async (orderedIds: string[]) => {
        const previous = users;
        setUsers(prev =>
            prev.map(u => {
                const index = orderedIds.indexOf(u.id);
                return index === -1 ? u : { ...u, sort_order: index };
            })
        );

        try {
            const results = await Promise.all(
                orderedIds.map((id, index) =>
                    supabase.from('Hairdressers').update({ sort_order: index }).eq('id', id)
                )
            );
            const failed = results.find(r => r.error);
            if (failed?.error) throw new Error(failed.error.message);
            return { success: true, error: '' };
        } catch (error: any) {
            console.error("Errore nel salvataggio dell'ordine dello staff:", error);
            setUsers(previous);
            return { success: false, error: error?.message || 'Errore sconosciuto' };
        }
    };

    const value = {
        users,
        invites,
        inviteUser,
        deleteInvite,
        updateUser,
        updateStaffOrder,
        deleteUser,
        getUserById,
        uploadShopBanner,
        deleteShopBanner,
        uploadShopLogo,
        deleteShopLogo,
        gallery,
        uploadGalleryImage,
        deleteGalleryImage,
    };

    return (
        <UsersContext.Provider value={value}>
            {children}
        </UsersContext.Provider>
    );
};